"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useForm, type Resolver, type SubmitHandler } from "react-hook-form";
import {
  applyAuthLoginEnvelope,
  getEmailVerificationPathIfNeeded,
  navigateAfterLogin,
} from "@/features/auth/lib/applyAuthLoginEnvelope";
import { useLoginMutation } from "@/hooks/useUser";
import { useAuthStore } from "@/store/auth.store";
import { useRouter } from "next/navigation";
import { loginSchema, type LoginFormInput } from "../schemas/login.schema";

export const useLoginForm = () => {
  const setUser = useAuthStore((s) => s.setUser);
  const router = useRouter();
  const loginMutation = useLoginMutation();

  const form = useForm<LoginFormInput>({
    resolver: zodResolver(loginSchema) as Resolver<LoginFormInput>,
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit: SubmitHandler<LoginFormInput> = (data) => {
    form.clearErrors("root");

    loginMutation.mutate(
      {
        email: data.email.trim(),
        password: data.password,
        rememberMe: false,
      },
      {
        onSuccess: (resBody) => {
          const verifyPath = getEmailVerificationPathIfNeeded(resBody);
          if (verifyPath) {
            router.push(verifyPath);
            return;
          }

          const result = applyAuthLoginEnvelope(resBody);
          if (!result.ok) {
            form.setError("root", { message: result.message });
            return;
          }

          setUser(result.user);
          navigateAfterLogin(result.user, router);
        },
        onError: (e) => {
          const message = axios.isAxiosError(e)
            ? String(
                (e.response?.data as { message?: string })?.message ??
                  e.message ??
                  "Login failed.",
              )
            : "Login failed.";
          form.setError("root", { message });
        },
      },
    );
  };

  return { form, onSubmit, isSubmitPending: loginMutation.isPending };
};
